import { Upload, X } from "lucide-react";
import * as React from "react";

import { cn } from "@/shared/lib/utils";
import {
  COMPROBANTE_ACCEPT,
  DEFAULT_MAX_SIZE_MB,
  type FileValidationFailure,
  type FileValidationResult,
  validateComprobanteFile,
} from "@/shared/utils/fileValidation";

export interface ColocaFileUploadLabels {
  /** Instruction when idle — e.g. "Click to upload or drag here". */
  instruction: string;
  /** Instruction while a file is dragged over the dropzone — e.g. "Drop the file here". */
  instructionDrop: string;
  /** First helper line below the instruction — e.g. "Upload a voucher of the transaction". */
  helper?: string;
  /** Second helper line — e.g. "Max 10 MB • Images (JPG, PNG, WebP)". */
  constraints?: string;
  /** Label of the "replace file" action shown once a file is selected. */
  change: string;
  /** Label of the "remove file" action shown once a file is selected. */
  remove: string;
  /** Alt text for the image preview. */
  previewAlt?: string;
}

export interface ColocaFileUploadProps extends Omit<
  React.ComponentPropsWithoutRef<"div">,
  "onChange" | "defaultValue"
> {
  /** Currently selected file. The component is fully controlled. */
  value: File | null;
  /** Called with the selected file, or `null` when the user removes it. */
  onChange: (file: File | null) => void;
  /** Translated texts — the component renders no hardcoded copy. */
  labels: ColocaFileUploadLabels;
  /** Accepted MIME types for the native file input. @default image types (JPG, PNG, WebP) */
  accept?: string;
  /** Maximum file size in megabytes. @default 10 */
  maxSizeMB?: number;
  /**
   * Replaces the default image validator (`validateComprobanteFile`). Pair it with `onReject` so a refused
   * file is explained instead of vanishing — `validateSpreadsheetFile` for batch templates, for instance.
   */
  validate?: (file: File) => FileValidationResult;
  /** Called with the failure when a picked file is refused; translate `result.code` for the person. */
  onReject?: (result: FileValidationFailure, file: File) => void;
  /**
   * Replaces the image preview in the selected state — name, size and an icon for documents and spreadsheets,
   * which have no thumbnail. The change/remove actions stay.
   */
  renderPreview?: (file: File) => React.ReactNode;
  /** Marks the dropzone visually invalid — wire to the form error state. */
  invalid?: boolean;
  disabled?: boolean;
}

/**
 * Single-file dropzone: click to browse or drag & drop, with image preview and change/remove actions once a
 * file is selected. Size and type validation run on selection; a refused file is never committed and is
 * reported through `onReject`, so the consumer can say why.
 *
 * The component is controlled (`value` / `onChange`) and copy-free — every visible string arrives through
 * `labels`, so the consumer translates them. For React Hook Form integration use `ColocaFileUploadField`.
 *
 * @summary controlled single-file dropzone with preview and translated labels
 */
export const ColocaFileUpload = React.forwardRef<HTMLDivElement, ColocaFileUploadProps>(
  function ColocaFileUpload(
    {
      value,
      onChange,
      labels,
      accept = COMPROBANTE_ACCEPT,
      maxSizeMB = DEFAULT_MAX_SIZE_MB,
      validate,
      onReject,
      renderPreview,
      invalid = false,
      disabled = false,
      className,
      ...rest
    },
    ref,
  ) {
    const [dragActive, setDragActive] = React.useState(false);
    const [previewUrl, setPreviewUrl] = React.useState<string | null>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const hasCustomPreview = renderPreview !== undefined;

    React.useEffect(() => {
      if (hasCustomPreview || !value || !value.type.startsWith("image/")) {
        setPreviewUrl(null);
        return;
      }
      const url = URL.createObjectURL(value);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }, [value, hasCustomPreview]);

    const commitFile = (file: File | null) => {
      if (!file) {
        onChange(null);
        return;
      }
      const result = validate ? validate(file) : validateComprobanteFile(file, maxSizeMB);
      if (!result.valid) {
        if (inputRef.current) inputRef.current.value = "";
        onReject?.(result, file);
        return;
      }
      onChange(file);
    };

    const handleDrag = (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      if (event.type === "dragenter" || event.type === "dragover") setDragActive(true);
      else if (event.type === "dragleave") setDragActive(false);
    };

    const handleDrop = (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      setDragActive(false);
      if (disabled || !event.dataTransfer.files?.length) return;
      commitFile(event.dataTransfer.files[0]);
    };

    const removeFile = () => {
      if (disabled) return;
      onChange(null);
      if (inputRef.current) inputRef.current.value = "";
    };

    return (
      <div ref={ref} className={cn("space-y-2", className)} {...rest}>
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          disabled={disabled}
          onChange={(event) => {
            if (disabled) return;
            commitFile(event.target.files?.[0] ?? null);
          }}
          className="sr-only"
        />

        {!value ? (
          <div
            role="button"
            tabIndex={disabled ? -1 : 0}
            aria-disabled={disabled}
            onClick={() => !disabled && inputRef.current?.click()}
            onKeyDown={(event) => {
              if (disabled) return;
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                inputRef.current?.click();
              }
            }}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
            className={cn(
              "group flex cursor-pointer flex-col items-center justify-center space-y-3 rounded-xl border-2 border-dashed border-border bg-muted/30 px-6 py-10 transition-all",
              !disabled && "hover:bg-muted/60",
              dragActive && !disabled && "scale-[1.01] border-primary bg-primary/5",
              invalid && "border-destructive bg-destructive/5",
              disabled && "cursor-not-allowed opacity-50",
            )}
          >
            <div
              className={cn(
                "rounded-full p-3 transition-transform group-hover:scale-110",
                dragActive ? "bg-primary/20" : "bg-primary/10",
              )}
            >
              <Upload aria-hidden="true" className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-1 text-center">
              <p className="text-sm font-semibold text-foreground">
                {dragActive ? labels.instructionDrop : labels.instruction}
              </p>
              {labels.helper && <p className="text-xs text-muted-foreground">{labels.helper}</p>}
              {labels.constraints && <p className="text-xs text-muted-foreground">{labels.constraints}</p>}
            </div>
          </div>
        ) : (
          <div className="overflow-hidden rounded-xl border border-border bg-muted/30">
            {renderPreview ? (
              renderPreview(value)
            ) : (
              <>
                {previewUrl && (
                  <img
                    src={previewUrl}
                    alt={labels.previewAlt ?? value.name}
                    className="h-40 w-full bg-card object-contain"
                  />
                )}
                <p className="truncate px-4 py-2 text-sm text-muted-foreground">{value.name}</p>
              </>
            )}
            <div className="flex gap-2 border-t border-border/50 p-3">
              <button
                type="button"
                onClick={() => inputRef.current?.click()}
                disabled={disabled}
                className="flex-1 text-center text-sm font-medium text-primary hover:underline disabled:cursor-not-allowed disabled:opacity-50"
              >
                {labels.change}
              </button>
              <button
                type="button"
                onClick={removeFile}
                disabled={disabled}
                className="flex items-center gap-1 text-sm font-medium text-destructive hover:underline disabled:opacity-50"
              >
                <X aria-hidden="true" className="h-4 w-4" />
                {labels.remove}
              </button>
            </div>
          </div>
        )}
      </div>
    );
  },
);
